import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tidePointsApi } from '../../services/api';
import {
  TidePointDto,
  CreateTidePointRequest,
  TidePointStatsDto,
  TideData
} from '../../types/TidePoint';
import TidePointMap from './TidePointMap';
import TidePointsManagementMap from './TidePointsManagementMap';
import TideChart from './TideChart';

const TidePointsPage: React.FC = () => {
  const [tidePoints, setTidePoints] = useState<TidePointDto[]>([]);
  const [stats, setStats] = useState<TidePointStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPoint, setEditingPoint] = useState<TidePointDto | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPoint, setViewingPoint] = useState<TidePointDto | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState<CreateTidePointRequest>({
    name: '',
    latitude: 0,
    longitude: 0,
    isActive: true,
    fetchTideData: true
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000); // Auto-dismiss after 5 seconds
  };

  const loadTidePoints = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tidePointsApi.getTidePoints({
        page,
        pageSize,
        search: searchTerm || undefined,
        includeDeleted
      });

      setTidePoints(response.tidePoints);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Failed to load tide points', err);
      showNotification('Failed to load tide points', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, includeDeleted]);

  const loadStats = async () => {
    try {
      const statsData = await tidePointsApi.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  useEffect(() => {
    loadTidePoints();
    loadStats();
  }, [loadTidePoints]);

  const handleSearch = () => {
    setPage(1);
    loadTidePoints();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setIncludeDeleted(false);
    setPage(1);
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      latitude: 0,
      longitude: 0,
      isActive: true,
      fetchTideData: true
    });
  };

  const handleCreate = () => {
    resetFormData();
    setEditingPoint(null);
    setShowCreateModal(true);
  };

  const handleCreateFromMap = (lat: number, lng: number) => {
    setFormData({
      name: '',
      latitude: lat,
      longitude: lng,
      isActive: true,
      fetchTideData: true
    });
    setEditingPoint(null);
    setShowCreateModal(true);
  };

  const handleEdit = (point: TidePointDto) => {
    setFormData({
      name: point.name,
      latitude: point.latitude,
      longitude: point.longitude,
      isActive: point.isActive,
      fetchTideData: false // Don't re-fetch when editing
    });
    setEditingPoint(point);
    setShowCreateModal(true);
  };

  const handleView = (point: TidePointDto) => {
    setViewingPoint(point);
    setShowViewModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name || formData.latitude === 0 || formData.longitude === 0) {
        showNotification('Please provide a name and select a location on the map', 'error');
        return;
      }

      setSaving(true);

      if (editingPoint) {
        await tidePointsApi.updateTidePoint(editingPoint.id, formData);
        showNotification('Tide point updated successfully', 'success');
      } else {
        // Creating new point - this will trigger NIWA API call on backend
        // This fetches a full year of tide data (12 API calls), so it may take 10-15 seconds
        await tidePointsApi.createTidePoint(formData);
        showNotification('Tide point created successfully! Tide data has been fetched from NIWA API.', 'success');
      }
      setShowCreateModal(false);
      resetFormData();
      setEditingPoint(null);
      await loadTidePoints();
      await loadStats();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to save tide point';
      showNotification(errorMsg, 'error');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async (id: string) => {
    if (!window.confirm('This will fetch new tide data from NIWA API for the next year. Continue?')) return;

    try {
      setRefreshing(id);
      await tidePointsApi.refreshTideData(id);
      showNotification('Tide data refreshed successfully!', 'success');
      await loadTidePoints();
      await loadStats();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to refresh tide data';
      showNotification(errorMsg, 'error');
      console.error(err);
    } finally {
      setRefreshing(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this tide point?')) return;

    try {
      await tidePointsApi.deleteTidePoint(id);
      showNotification('Tide point deleted successfully', 'success');
      await loadTidePoints();
      await loadStats();
    } catch (err) {
      showNotification('Failed to delete tide point', 'error');
      console.error(err);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await tidePointsApi.restoreTidePoint(id);
      showNotification('Tide point restored successfully', 'success');
      await loadTidePoints();
      await loadStats();
    } catch (err) {
      showNotification('Failed to restore tide point', 'error');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPoints.size === 0) {
      showNotification('No tide points selected', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedPoints.size} tide points?`)) return;

    try {
      const pointIds = Array.from(selectedPoints);
      await tidePointsApi.bulkDeleteTidePoints(pointIds);
      setSelectedPoints(new Set());
      showNotification(`Successfully deleted ${pointIds.length} tide points`, 'success');
      await loadTidePoints();
      await loadStats();
    } catch (err) {
      showNotification('Failed to delete tide points', 'error');
      console.error(err);
    }
  };

  const togglePointSelection = (pointId: string) => {
    const newSelection = new Set(selectedPoints);
    if (newSelection.has(pointId)) {
      newSelection.delete(pointId);
    } else {
      newSelection.add(pointId);
    }
    setSelectedPoints(newSelection);
  };

  const parseTideData = (json?: string): TideData | null => {
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch (e) {
      console.error('Failed to parse tide data', e);
      return null;
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading && tidePoints.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-sm rounded-xl border border-gray-200">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Loading Tide Points...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Notification Banner */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg border flex items-start justify-between animate-fade-in ${
            notification.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : notification.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-start gap-3">
              {notification.type === 'success' && (
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            ← Back to Dashboard
          </Link>
          <div className="flex justify-between items-center mt-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Tide Points Management</h1>
              <p className="mt-2 text-gray-600">
                Manage ocean tide monitoring points for the mobile app
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Tide Point
            </button>
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-gray-900">{stats.totalPoints}</div>
              <div className="text-sm text-gray-500">Total Points</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{stats.activePoints}</div>
              <div className="text-sm text-gray-500">Active Points</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{stats.deletedPoints}</div>
              <div className="text-sm text-gray-500">Deleted Points</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{stats.pointsNeedingRefresh}</div>
              <div className="text-sm text-gray-500">Need Refresh</div>
            </div>
          </div>
        )}

        {/* Map showing all tide points */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <TidePointsManagementMap
            tidePoints={tidePoints}
            onTidePointClick={handleView}
            onMapClick={handleCreateFromMap}
            height="500px"
          />
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters & Search</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 px-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm">Include Deleted</span>
              </label>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedPoints.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-xl mb-6">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedPoints.size} points selected
              </span>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Tide Points Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tidePoints.map((point) => {
            const tideData = parseTideData(point.tideDataJson);
            const needsRefresh = point.nextRefreshDate && new Date(point.nextRefreshDate) < new Date();

            return (
              <div
                key={point.id}
                className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-lg flex flex-col h-full ${
                  point.deleted
                    ? 'border-red-200 bg-red-50'
                    : selectedPoints.has(point.id)
                    ? 'border-indigo-300 shadow-sm'
                    : 'border-gray-200'
                }`}
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedPoints.has(point.id)}
                        onChange={() => togglePointSelection(point.id)}
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <h3 className="font-semibold text-lg text-gray-900">{point.name}</h3>
                    </div>
                    <div className="flex flex-col gap-1">
                      {point.deleted ? (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          Deleted
                        </span>
                      ) : point.isActive ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                          Inactive
                        </span>
                      )}
                      {needsRefresh && !point.deleted && (
                        <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                          Needs Refresh
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="text-sm text-gray-600 space-y-1 mb-4">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Lat: {point.latitude.toFixed(4)}, Lng: {point.longitude.toFixed(4)}</span>
                    </div>
                    {tideData && (
                      <div className="text-xs text-gray-500 mt-2">
                        <div>{tideData.tideEvents.length} tide events loaded</div>
                        <div>Data fetched: {formatDate(point.dataFetchedAt)}</div>
                        <div>Next refresh: {formatDate(point.nextRefreshDate)}</div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-auto pt-2">
                    <button
                      onClick={() => handleView(point)}
                      className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      View
                    </button>
                    {!point.deleted && (
                      <>
                        <button
                          onClick={() => handleEdit(point)}
                          className="flex-1 px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRefresh(point.id)}
                          disabled={refreshing === point.id}
                          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                          title="Refresh tide data from NIWA"
                        >
                          {refreshing === point.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-700 border-t-transparent rounded-full"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(point.id)}
                          className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                    {point.deleted && (
                      <button
                        onClick={() => handleRestore(point.id)}
                        className="flex-1 px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {tidePoints.length === 0 && !loading && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tide points found</h3>
            <p className="text-gray-500 mb-4">Get started by creating your first tide monitoring point</p>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Add Tide Point
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">
                  {editingPoint ? 'Edit Tide Point' : 'Create New Tide Point'}
                </h2>

                {saving && !editingPoint && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="animate-spin h-5 w-5 text-blue-600 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <div>
                        <p className="font-medium text-blue-900">Fetching tide data from NIWA...</p>
                        <p className="text-sm text-blue-700 mt-1">
                          This may take 10-15 seconds as we fetch a full year of tide data. Please wait...
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Map Section - Now comes first */}
                  <div>
                    <TidePointMap
                      onLocationChange={(lat, lng) => {
                        setFormData({ ...formData, latitude: lat, longitude: lng });
                      }}
                      initialLat={editingPoint?.latitude || formData.latitude || undefined}
                      initialLng={editingPoint?.longitude || formData.longitude || undefined}
                      height="500px"
                    />
                  </div>

                  {/* Point Name - Now comes after map */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Point Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Wellington Harbour Tide Point"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Settings */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                        Active (visible in mobile app)
                      </label>
                    </div>

                    {!editingPoint && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="fetchTideData"
                          checked={formData.fetchTideData}
                          onChange={(e) => setFormData({ ...formData, fetchTideData: e.target.checked })}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label htmlFor="fetchTideData" className="text-sm font-medium text-gray-700">
                          Fetch tide data from NIWA API (recommended)
                        </label>
                      </div>
                    )}

                    {!editingPoint && formData.fetchTideData && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                        <strong>Note:</strong> Creating this tide point will automatically fetch a full year of tide data from the NIWA API. This may take 10-15 seconds.
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetFormData();
                      setEditingPoint(null);
                    }}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {saving
                      ? (editingPoint ? 'Updating...' : 'Creating & fetching tide data...')
                      : (editingPoint ? 'Update' : 'Create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && viewingPoint && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <h2 className="text-2xl font-bold">{viewingPoint.name}</h2>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewingPoint(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Latitude</h3>
                      <p className="text-gray-900">{viewingPoint.latitude}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Longitude</h3>
                      <p className="text-gray-900">{viewingPoint.longitude}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Status</h3>
                      <p className="text-gray-900">
                        {viewingPoint.deleted ? 'Deleted' : viewingPoint.isActive ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Created</h3>
                      <p className="text-gray-900">{formatDate(viewingPoint.createdAt)}</p>
                    </div>
                  </div>

                  {viewingPoint.dataFetchedAt && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Data Fetched</h3>
                        <p className="text-gray-900">{formatDate(viewingPoint.dataFetchedAt)}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Next Refresh</h3>
                        <p className="text-gray-900">{formatDate(viewingPoint.nextRefreshDate)}</p>
                      </div>
                    </div>
                  )}

                  {viewingPoint.tideDataJson && (() => {
                    const tideData = parseTideData(viewingPoint.tideDataJson);
                    if (!tideData) return <p className="text-gray-500">Unable to parse tide data</p>;
                    return <TideChart tideData={tideData} />;
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TidePointsPage;
